import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coins, Gauge, Network, IndianRupee, PlugZap, BadgeCheck } from "lucide-react";
import ChargeHubEnergyFlow from "../chargehub/ChargeHubEnergyFlow";
import { useT } from "../i18n/LanguageContext";

const CHALLENGES = [
  { icon: Coins, title: "Energy cost", body: "Charging demand and electricity supply do not always align in the most economical way." },
  { icon: Gauge, title: "Site capacity", body: "Available grid and site infrastructure can limit charging-site operation and expansion." },
  { icon: Network, title: "Energy coordination", body: "Solar, storage, chargers and existing site loads are often managed as separate systems." },
];

const OUTCOMES = [
  { icon: IndianRupee, title: "Lower energy cost", body: "Improve how grid electricity, solar generation and available energy resources are used across the charging site." },
  { icon: PlugZap, title: "More usable capacity", body: "Help charging sites operate more effectively within available grid and site constraints." },
  { icon: BadgeCheck, title: "Verified performance", body: "Provide visibility into how the complete charging energy system is performing and measure the value created through optimisation." },
];

function Eyebrow({ children, color = "#00D4FF" }) {
  return <div className="text-xs font-medium tracking-[0.2em] uppercase mb-3" style={{ color }}>{children}</div>;
}

function IconCard({ icon: Icon, title, body, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-card-soft">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF] mb-4">
        <Icon size={19} />
      </div>
      <h3 className="font-heading text-base text-graphite">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

export default function ChargeHub() {
  const t = useT();
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "WattZap ChargeHub | EV Charging Energy Optimisation";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Explore WattZap ChargeHub, a future energy-optimisation solution designed to coordinate grid supply, solar PV, battery storage and EV charging demand.");
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const scrollToOpportunity = () => {
    document.getElementById("chargehub-opportunity")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="chargehub-page">
      {/* 1 — HERO */}
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span data-testid="chargehub-hero-badge" className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium bg-[#FFC857]/10 border border-[#FFC857]/40 text-[#FFC857]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFC857]" /> {t("IN DEVELOPMENT · PILOT PARTNERSHIPS")}
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{t("Improve the energy economics behind every charging session")}</h1>
          <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">
            {t("WattZap ChargeHub is being explored as a vendor-neutral energy-optimisation layer for EV charging stations and fleet depots. The ambition is to coordinate grid supply, solar generation, battery storage and charger demand to improve site energy performance and make better use of available infrastructure.")}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/contact?topic=chargehub" data-testid="chargehub-hero-cta-primary" className="btn-gradient inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
              {t("Discuss a ChargeHub Pilot")} <ArrowRight size={16} />
            </Link>
            <button type="button" onClick={scrollToOpportunity} data-testid="chargehub-hero-cta-secondary" className="btn-outline-brand inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm">
              {t("See the Opportunity")}
            </button>
          </div>
        </div>
      </section>

      {/* 2 — THE CHALLENGE */}
      <section id="chargehub-opportunity" className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Eyebrow>{t("The opportunity")}</Eyebrow>
            <h2 className="font-heading text-3xl lg:text-4xl text-graphite">{t("Charging stations are becoming complex energy sites")}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("Charging-site performance depends on more than the charger itself. Grid limitations, variable charging demand, energy cost, solar availability and site infrastructure can all influence the economics of each charging session.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="chargehub-challenges">
            {CHALLENGES.map((c) => <IconCard key={c.title} icon={c.icon} title={t(c.title)} body={t(c.body)} testId={`chargehub-challenge-${c.title.toLowerCase().replace(/\s+/g, "-")}`} />)}
          </div>
        </div>
      </section>

      {/* 3 — ONE COORDINATED SYSTEM */}
      <section className="bg-[#071A2D] text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Eyebrow>{t("One coordinated system")}</Eyebrow>
            <h2 className="font-heading text-3xl lg:text-4xl">{t("One intelligent energy layer behind the charging site")}</h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              {t("WattZap ChargeHub aims to create a coordinated energy layer connecting the site's grid supply, solar PV, battery storage and EV chargers. The solution would use site data and intelligent controls to support more efficient and measurable charging-site operation.")}
            </p>
          </div>
          <div className="max-w-3xl">
            <ChargeHubEnergyFlow testId="chargehub-page-flow" />
          </div>
        </div>
      </section>

      {/* 4 — WHAT OPERATORS CAN EXPECT */}
      <section className="bg-mist py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Eyebrow>{t("What operators can expect")}</Eyebrow>
            <h2 className="font-heading text-3xl lg:text-4xl text-graphite">{t("Designed around charging-site outcomes")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="chargehub-outcomes">
            {OUTCOMES.map((o) => <IconCard key={o.title} icon={o.icon} title={t(o.title)} body={t(o.body)} testId={`chargehub-outcome-${o.title.toLowerCase().replace(/\s+/g, "-")}`} />)}
          </div>
          <p data-testid="chargehub-outcomes-qualifier" className="mt-8 text-sm text-slate-500 max-w-2xl">
            {t("The opportunity and solution design will depend on actual site utilisation, infrastructure, energy data and operating requirements.")}
          </p>
        </div>
      </section>

      {/* 5 — PILOT INVITATION */}
      <section className="bg-[#071A2D] text-white py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-xs font-medium tracking-[0.2em] uppercase mb-3 text-[#FFC857]">{t("Pilot partnerships")}</div>
          <h2 className="font-heading text-3xl sm:text-4xl">{t("Interested in exploring a smarter energy system for your charging site?")}</h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
            {t("WattZap is seeking a small number of Charge Point Operators, fleet operators, depot owners and commercial charging-site partners interested in exploring how integrated energy optimisation could improve their operations. Each potential pilot will begin with a confidential site-level assessment before any solution or investment is proposed.")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact?topic=chargehub" data-testid="chargehub-final-cta-primary" className="btn-gradient inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm">
              {t("Become a ChargeHub Pilot Partner")} <ArrowRight size={16} />
            </Link>
            <Link to="/contact?topic=chargehub" data-testid="chargehub-final-cta-secondary" className="btn-outline-brand inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm">
              {t("Talk to WattZap")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
